import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { REASONS, validateContact } from '@/lib/contact-schema.js'
import { cn } from '@/lib/cn'

type Values = {
  name: string
  email: string
  company: string
  reason: string
  message: string
}

const EMPTY: Values = {
  name: '',
  email: '',
  company: '',
  reason: REASONS[0],
  message: '',
}

const FIELD_ORDER: (keyof Values)[] = ['name', 'email', 'company', 'reason', 'message']

const labelClass = 'block text-[13px] font-bold tracking-[-0.01em] text-ink'
const controlClass =
  'mt-2 block w-full rounded-card border bg-white px-4 py-3 text-[14.5px] text-ink ' +
  'transition-colors placeholder:text-muted-soft focus:border-primary focus:outline-none'

/**
 * The form validates and composes a message; it does not send one.
 *
 * This site is a static frontend with no submission endpoint, and the design
 * is explicit that a form which pretends to send is worse than one that says
 * so. The composed text is offered for copying instead.
 */
export function ContactForm() {
  const [searchParams] = useSearchParams()
  const intent = searchParams.get('intent')

  const [values, setValues] = useState<Values>(() => ({
    ...EMPTY,
    // The "Become a design partner" CTA lands here with the reason preselected.
    reason: intent === 'partner' ? REASONS[0] : EMPTY.reason,
  }))
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>({})
  const [composed, setComposed] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const formRef = useRef<HTMLFormElement>(null)
  const summaryRef = useRef<HTMLDivElement>(null)
  const readyRef = useRef<HTMLDivElement>(null)

  const messageText = useMemo(
    () =>
      [
        `Name: ${values.name}`,
        `Email: ${values.email}`,
        values.company ? `Company: ${values.company}` : null,
        `Reason: ${values.reason}`,
        '',
        values.message,
      ]
        .filter((line): line is string => line !== null)
        .join('\n'),
    [values],
  )

  const setField = (field: keyof Values, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    // Clear a field error as soon as the user edits that field.
    setErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setCopyState('idle')

    const result = validateContact(values)

    if (!result.valid) {
      setErrors(result.errors)
      setComposed(false)
      // Announce the failure, then move focus to the field that needs fixing.
      summaryRef.current?.focus()
      const first = FIELD_ORDER.find((field) => result.errors[field])
      const el = first ? formRef.current?.elements.namedItem(first) : null
      if (el instanceof HTMLElement) el.focus()
      return
    }

    setErrors({})
    setComposed(true)
    // Move focus to the panel so the outcome is announced, not just painted.
    window.requestAnimationFrame(() => readyRef.current?.focus())
  }

  async function copyMessage() {
    try {
      await navigator.clipboard.writeText(messageText)
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  const errorList = FIELD_ORDER.filter((field) => errors[field])

  if (composed) {
    return (
      <div
        ref={readyRef}
        tabIndex={-1}
        role="status"
        className="rounded-card border border-computes/30 bg-computes/[0.06] p-6 focus:outline-none sm:p-8"
      >
        <h3 className="text-[19px] font-extrabold tracking-[-0.02em]">
          Your message is composed and validated. It has not been sent.
        </h3>
        <p className="mt-4 max-w-measure text-[14px] leading-[1.75] text-muted">
          There is no submission endpoint wired to this form, and a form that pretends to send is
          worse than one that says so. Copy the message and send it through whichever channel you
          already have with us.
        </p>

        <pre className="scroll-x mt-6 rounded-card border border-line bg-white p-5 font-mono text-[12.5px] leading-[1.7] text-ink">
          {messageText}
        </pre>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button type="button" onClick={copyMessage}>
            {copyState === 'copied' ? 'Copied' : 'Copy message'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setComposed(false)}>
            Keep editing
          </Button>
          <p aria-live="polite" className="text-[12.5px] text-muted">
            {copyState === 'copied' ? 'Copied to your clipboard.' : null}
            {copyState === 'failed'
              ? 'Copying is blocked in this browser — select the text above and copy it manually.'
              : null}
          </p>
        </div>
      </div>
    )
  }

  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate className="grid gap-5">
      {/* Error summary — focusable so validation failures are announced. */}
      <div
        ref={summaryRef}
        tabIndex={-1}
        role="alert"
        className={cn(
          'rounded-card border p-4 focus:outline-none',
          errorList.length
            ? 'border-[#d64545]/30 bg-[#d64545]/[0.06]'
            : 'sr-only border-transparent',
        )}
      >
        {errorList.length ? (
          <>
            <p className="text-[13.5px] font-bold text-[#a02c2c]">
              {errorList.length === 1
                ? 'One field needs attention:'
                : `${errorList.length} fields need attention:`}
            </p>
            <ul className="mt-2 grid gap-1">
              {errorList.map((field) => (
                <li key={field} className="text-[13px] leading-[1.6] text-[#a02c2c]">
                  {errors[field]}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div>
        <label htmlFor="c-name" className={labelClass}>
          Your name
        </label>
        <input
          id="c-name"
          name="name"
          type="text"
          autoComplete="name"
          value={values.name}
          onChange={(event) => setField('name', event.target.value)}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={errors.name ? 'c-name-err' : undefined}
          className={cn(controlClass, errors.name ? 'border-[#d64545]' : 'border-line')}
        />
        {errors.name ? (
          <p id="c-name-err" className="mt-2 text-[12.5px] font-semibold text-[#a02c2c]">
            {errors.name}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="c-email" className={labelClass}>
          Email
        </label>
        <input
          id="c-email"
          name="email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={(event) => setField('email', event.target.value)}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? 'c-email-err' : undefined}
          className={cn(controlClass, errors.email ? 'border-[#d64545]' : 'border-line')}
        />
        {errors.email ? (
          <p id="c-email-err" className="mt-2 text-[12.5px] font-semibold text-[#a02c2c]">
            {errors.email}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="c-company" className={labelClass}>
          Company <span className="font-medium text-muted-soft">optional</span>
        </label>
        <input
          id="c-company"
          name="company"
          type="text"
          autoComplete="organization"
          value={values.company}
          onChange={(event) => setField('company', event.target.value)}
          aria-invalid={errors.company ? true : undefined}
          aria-describedby={errors.company ? 'c-company-err' : undefined}
          className={cn(controlClass, errors.company ? 'border-[#d64545]' : 'border-line')}
        />
        {errors.company ? (
          <p id="c-company-err" className="mt-2 text-[12.5px] font-semibold text-[#a02c2c]">
            {errors.company}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="c-reason" className={labelClass}>
          Why you are writing
        </label>
        <select
          id="c-reason"
          name="reason"
          value={values.reason}
          onChange={(event) => setField('reason', event.target.value)}
          aria-invalid={errors.reason ? true : undefined}
          aria-describedby={errors.reason ? 'c-reason-err' : undefined}
          className={cn(controlClass, errors.reason ? 'border-[#d64545]' : 'border-line')}
        >
          {REASONS.map((reason) => (
            <option key={reason} value={reason}>
              {reason}
            </option>
          ))}
        </select>
        {errors.reason ? (
          <p id="c-reason-err" className="mt-2 text-[12.5px] font-semibold text-[#a02c2c]">
            {errors.reason}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="c-message" className={labelClass}>
          What you have, or what you want to know
        </label>
        <textarea
          id="c-message"
          name="message"
          rows={6}
          value={values.message}
          onChange={(event) => setField('message', event.target.value)}
          placeholder="e.g. Tally trial balance, quarterly, header block on the first six rows, figures in lakh."
          aria-invalid={errors.message ? true : undefined}
          aria-describedby={errors.message ? 'c-message-err' : undefined}
          className={cn(
            controlClass,
            'resize-y',
            errors.message ? 'border-[#d64545]' : 'border-line',
          )}
        />
        {errors.message ? (
          <p id="c-message-err" className="mt-2 text-[12.5px] font-semibold text-[#a02c2c]">
            {errors.message}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg">
          Compose message
        </Button>
        <p className="text-[12.5px] text-muted-soft">No tracking on this form.</p>
      </div>
    </form>
  )
}
